import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Tag } from "primereact/tag";
import { useMemo, useState } from "react";
import {
  useGetContractHierarchyRulesByContractQuery,
  useCreateContractHierarchyRuleMutation,
} from "store/queries/contractHierarchyRules";
import { useGetAllPackageRulesetsQuery } from "store/queries/packageRuleset";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetCaseRateRulesetsQuery } from "store/queries/pricing";
import { useGetAllDiscountRulesetsQuery } from "store/queries/procedureDiscountRuleset";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import { ITriggerRulesetType } from "types/ContractHierarchyRule/ContractHierarchyRule";

import { HierarchyRuleCard } from "./HierarchyRuleCard";

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const SelectorRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`;

const AddRuleForm = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 6px;
`;

interface IRulesetOption {
  label: string;
  value: string;
  rulesetType: ITriggerRulesetType;
}

export function HierarchyRulesManager() {
  const [selectedProviderId, setSelectedProviderId] = useState<string>("");
  const [selectedPayerId, setSelectedPayerId] = useState<number | null>(null);
  const [selectedRuleset, setSelectedRuleset] = useState<string | null>(null);
  const [newPriority, setNewPriority] = useState<number | null>(1);

  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();
  const { data: mpprRulesets } = useGetAllDiscountRulesetsQuery();
  const { data: caseRateRulesets } = useGetCaseRateRulesetsQuery();
  const { data: packageRulesets } = useGetAllPackageRulesetsQuery();

  const hasSelection = selectedProviderId && selectedPayerId;

  const { data: rulesResponse, isLoading: rulesLoading } =
    useGetContractHierarchyRulesByContractQuery(
      { providerId: selectedProviderId, payerId: selectedPayerId! },
      { skip: !hasSelection }
    );

  const [createRule, { isLoading: isCreating }] =
    useCreateContractHierarchyRuleMutation();

  const rules = rulesResponse?.data || [];

  // Build provider and payer dropdown options
  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    if (!payers?.data) return [];
    return payers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [payers]);

  // Build ruleset options for this provider-payer pair
  const rulesetOptions: IRulesetOption[] = useMemo(() => {
    if (!hasSelection) return [];

    const options: IRulesetOption[] = [];

    // MPPR rulesets
    if (mpprRulesets?.data) {
      const filtered = mpprRulesets.data.filter(
        (r: any) =>
          r.providerId === selectedProviderId && r.payerId === selectedPayerId
      );
      filtered.forEach((r: any) => {
        options.push({
          label: `${r.groupName || "MPPR Ruleset"} (MPPR)`,
          value: r.id,
          rulesetType: "MPPR",
        });
      });
    }

    // Case Rate rulesets
    if (caseRateRulesets?.data) {
      const filtered = caseRateRulesets.data.filter(
        (r: any) =>
          r.providerId === selectedProviderId && r.payerId === selectedPayerId
      );
      filtered.forEach((r: any) => {
        options.push({
          label: `${r.groupName || "Case Rate"} (Case Rate)`,
          value: r.id,
          rulesetType: "Case Rate",
        });
      });
    }

    // Package rulesets
    if (packageRulesets?.data) {
      const filtered = packageRulesets.data.filter(
        (r: any) =>
          r.providerId === selectedProviderId && r.payerId === selectedPayerId
      );
      filtered.forEach((r: any) => {
        options.push({
          label: `${r.groupName || "Package"} (Package)`,
          value: r.id,
          rulesetType: "Package",
        });
      });
    }

    return options;
  }, [
    hasSelection,
    selectedProviderId,
    selectedPayerId,
    mpprRulesets,
    caseRateRulesets,
    packageRulesets,
  ]);

  const handleAddRule = async () => {
    if (!selectedRuleset || !newPriority || !hasSelection) return;

    const option = rulesetOptions.find((o) => o.value === selectedRuleset);
    if (!option) return;

    await createRule({
      providerId: selectedProviderId,
      payerId: selectedPayerId!,
      name: option.label,
      triggerRulesetType: option.rulesetType,
      triggerRulesetId: option.value,
      priority: newPriority,
      active: true,
      suppressions: [],
    });

    setSelectedRuleset(null);
    setNewPriority((rules.length + 2) as number);
  };

  const activeCount = rules.filter((r) => r.active).length;

  return (
    <div>
      <ConfirmDialog />
      <HeaderRow>
        <div className="flex align-items-center gap-2">
          <h2 className="m-0">Contract Hierarchy Rules</h2>
          {hasSelection && (
            <Tag
              value={`${activeCount} active rule${
                activeCount !== 1 ? "s" : ""
              }`}
              severity="info"
            />
          )}
        </div>
      </HeaderRow>

      <SelectorRow>
        <div className="flex flex-column gap-1">
          <label className="font-bold text-sm">Provider</label>
          <Dropdown
            value={selectedProviderId}
            options={providerOptions}
            onChange={(e) => {
              setSelectedProviderId(e.value);
              setSelectedRuleset(null);
            }}
            placeholder="Select Provider"
            filter
            className="w-20rem"
          />
        </div>
        <div className="flex flex-column gap-1">
          <label className="font-bold text-sm">Payer</label>
          <Dropdown
            value={selectedPayerId}
            options={payerOptions}
            onChange={(e) => {
              setSelectedPayerId(e.value);
              setSelectedRuleset(null);
            }}
            placeholder="Select Payer"
            filter
            className="w-20rem"
          />
        </div>
      </SelectorRow>

      {hasSelection && (
        <>
          <AddRuleForm>
            <div className="flex flex-column gap-1">
              <label className="font-bold text-sm">Trigger Ruleset</label>
              <Dropdown
                value={selectedRuleset}
                options={rulesetOptions}
                onChange={(e) => setSelectedRuleset(e.value)}
                placeholder="Select ruleset"
                className="w-20rem"
                emptyMessage="No rulesets for this provider-payer"
              />
            </div>
            <div className="flex flex-column gap-1" style={{ width: "8rem" }}>
              <label className="font-bold text-sm">Priority</label>
              <InputNumber
                value={newPriority}
                onValueChange={(e) => setNewPriority(e.value ?? null)}
                min={1}
                inputClassName="w-full"
              />
            </div>
            <Button
              label="Add Rule"
              icon="pi pi-plus"
              onClick={handleAddRule}
              disabled={!selectedRuleset || !newPriority || isCreating}
              loading={isCreating}
              style={{ flexShrink: 0 }}
            />
          </AddRuleForm>

          {rulesLoading && (
            <div className="flex justify-content-center p-4">
              <i className="pi pi-spin pi-spinner text-4xl" />
            </div>
          )}
          {!rulesLoading && rules.length === 0 && (
            <div className="text-center text-500 p-4">
              No hierarchy rules for this provider-payer pair.
            </div>
          )}
          {!rulesLoading &&
            rules.length > 0 &&
            rules.map((rule) => (
              <HierarchyRuleCard
                key={rule.id}
                rule={rule}
                allRules={rules}
                rulesetOptions={rulesetOptions}
              />
            ))}
        </>
      )}
    </div>
  );
}
